import React from "react";
import { MessageCircle, Send, FileText } from "lucide-react";
import { Card } from "../../common/Primitives";
import { formatDateTime } from "../../../lib/datetime";

/**
 * Founder ⇄ Reviewer message thread with attachment chips + reply composer.
 * All state lives in the parent (KycQueue) so replies re-fetch cleanly.
 */
export default function KycThreadCard({ thread, reply, setReply, onSend }) {
  return (
    <Card testId="kyc-thread-card">
      <div className="overline mb-3 flex items-center gap-2"><MessageCircle size={14} /> Founder ⇄ Reviewer Thread</div>
      <div className="space-y-2 mb-3 max-h-[280px] overflow-y-auto">
        {thread.length === 0 && (
          <div className="text-xs text-ink-muted italic" data-testid="kyc-thread-empty">
            No messages yet. Your next status note here will start the thread — founder replies in-app.
          </div>
        )}
        {thread.map((m) => {
          const mine = m.author_role === "reviewer";
          return (
            <div
              key={m.message_id}
              className={`p-2.5 rounded border text-sm ${mine
                ? "bg-navy/[0.03] border-navy/10 text-ink"
                : "bg-emerald-50 border-emerald-200 text-emerald-900"}`}
              data-testid={`kyc-thread-msg-${m.message_id}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                  {mine ? "Reviewer" : "Founder"} · {m.kind}
                </span>
                <span className="text-[10px] font-mono text-ink-muted">
                  {m.created_at ? formatDateTime(m.created_at) : ""}
                </span>
              </div>
              <div className="whitespace-pre-wrap text-sm">{m.body}</div>
              {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`kyc-thread-admin-attachments-${m.message_id}`}>
                  {m.attachments.map((a) => (
                    <span key={a.doc_id} className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-white border border-navy/10 text-ink inline-flex items-center gap-1">
                      <FileText size={10} /> {a.filename}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-end gap-2">
        <textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder="Reply to the founder — appears in their app and their bell"
          rows={2}
          className="flex-1 text-sm bg-white border border-navy/15 rounded p-2 text-ink focus:border-gold outline-none resize-none"
          data-testid="kyc-thread-reply-input"
        />
        <button
          onClick={onSend}
          disabled={!reply.trim()}
          className="px-3 py-2 rounded bg-navy text-white text-xs font-heading hover:bg-[#256E58] disabled:opacity-40 inline-flex items-center gap-1"
          data-testid="kyc-thread-reply-send"
        >
          <Send size={12} /> Send
        </button>
      </div>
    </Card>
  );
}
